<template>
    <div class="mb-3 container-fluid">
        <BreadcrumbNav />
    </div>
    <div class="container-fluid">
        <!-- Currently Editing Banner -->
        <div v-if="workgroupDetails && (workgroupDetails.id || workgroupDetails.workgroup_name)"
            class="alert alert-success text-white d-flex align-items-center my-2" role="alert">
            <i class="material-icons me-2">edit</i>
            <div>
                <strong>Currently Editing (Workgroup):</strong>
                <span v-if="workgroupDetails.id" class="ms-2">Workgroup ID: <strong>{{ workgroupDetails.id
                        }}</strong></span>
                <span v-if="workgroupDetails.workgroup_name" class="ms-2">- <strong>{{ workgroupDetails.workgroup_name
                        }}</strong></span>
            </div>
        </div>

        <!-- Tab Legend -->
        <div class="d-flex align-items-center gap-3 mb-2 text-xs text-secondary">
            <span><i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i> Required</span>
            <span><i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i> Optional</span>
        </div>

        <NavTabs>
            <NavTab target="workgroup-details" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Workgroup Details (Required)">
                    Details <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>

            <NavTab target="schedule-by-teams" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span :title="teamsEnabled ? 'Schedule by Teams (in sequential order) (Required)' : 'Schedule by Teams (in sequential order) (Optional — enable on Details tab)'">
                    Teams
                    <i v-if="teamsEnabled" class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                    <i v-else class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                    <i v-if="!teamsEnabled" class="material-icons text-warning" style="font-size: 12px; vertical-align: middle;" title="Teams scheduling is not enabled. Enable it on the Details tab.">warning</i>
                </span>
            </NavTab>

            <NavTab target="skilled-volunteers" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Preferred Volunteers who prefer this workgroup (Optional)">
                    Preferred Volunteers <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab target="instance-workgroups" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Activity Instance Workgroups (Optional)">
                    Instances <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab target="workgroup-admins" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Manage Workgroup Administrators (Required)">
                    Admins <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>

            <NavTab target="workgroup-preferences" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Workgroup Rules (Optional)">
                    Workgroup Rules <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>
        </NavTabs>

        <ContentContainer>
            <template #content>
                <ExplainPageButton :page-key="`workgroup.${activeTab}`" />
                <div v-if="activeTab === 'workgroup-details'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and edit the core settings for this workgroup, including name, skill, and scheduling options.</p>
                    <WorkgroupDetails />
                </div>

                <div v-if="activeTab === 'schedule-by-teams'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Organize volunteers into teams and define the scheduling order.</p>
                    <ScheduleByTeams :teamsEnabled="teamsEnabled" />
                </div>

                <div v-else-if="activeTab === 'skilled-volunteers'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View preferred volunteers who have the required skill for this workgroup.</p>
                    <SkilledVolunteers />
                </div>

                <div v-else-if="activeTab === 'instance-workgroups'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and manage the activity instance workgroups linked to this workgroup.</p>
                    <InstanceWorkgroups />
                </div>

                <div v-else-if="activeTab === 'workgroup-admins'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Assign or remove administrators who can manage this workgroup.</p>
                    <WorkgroupAdmins />
                </div>

                <div v-else-if="activeTab === 'workgroup-preferences'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Configure scheduling preferences and rules for this workgroup.</p>
                    <WorkgroupPreferences />
                </div>
            </template>
        </ContentContainer>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"

// Common shared components
import NavTab from "../../components/Tabs/NavTab.vue"
import NavTabs from "../../components/Tabs/NavTabs.vue"
import ContentContainer from "../../components/PageLayout/ContentContainer.vue"

// Workgroup section components
import WorkgroupDetails from "./edit-workgroup/WorkgroupDetails.vue"
import SkilledVolunteers from "./edit-workgroup/SkilledVolunteers.vue"
import InstanceWorkgroups from "./edit-workgroup/InstanceWorkgroups.vue"
import WorkgroupAdmins from "./edit-workgroup/WorkgroupAdmins.vue"
import WorkgroupPreferences from "./edit-workgroup/WorkgroupPreferences.vue"
import ScheduleByTeams from "./edit-workgroup/ScheduleByTeams.vue"
import BreadcrumbNav from "../../components/common/BreadcrumbNav.vue"
import ExplainPageButton from "@/components/common/ExplainPageButton.vue"
import { useBreadcrumb } from "@/composables/useBreadcrumb"

const store = useStore()
const route = useRoute()
const activeTab = ref("workgroup-details")
const workgroupDetails = ref(null)
const workgroupId = Number(route.params.workgroupid)

// Watch for changes in the store
const storeWorkgroupDetails = computed(() => {
    return store.getters.workgroupDetails || null
})

const teamsEnabled = computed(() => {
    return workgroupDetails.value?.schedule_by_teams_in_sequential_order ?? false
})

watch(storeWorkgroupDetails, (newValue) => {
    if (newValue) {
        workgroupDetails.value = newValue
    }
}, { immediate: true })

const { trail, setTrail } = useBreadcrumb()

onMounted(async () => {
    // Always fetch workgroup details to ensure data is loaded
    if (workgroupId) {
        try {
            const data = await store.dispatch("fetchWorkgroupById", workgroupId);
            if (data) {
                workgroupDetails.value = data
                // Fallback trail for direct-URL access
                if (!trail.value.length) {
                    setTrail([
                        { label: 'Activities', icon: 'list', route: { name: 'activities-events' } },
                        { label: data.workgroup_name || `Workgroup #${workgroupId}`, icon: 'group_work' },
                    ])
                }
            }
        } catch (error) {
            console.error("Error loading workgroup:", error);
        }
    }
})
</script>
